import { useState, useEffect, useCallback } from "react";
import { post } from "../../../../utils/fetch";
import { URL_CONCESIONARIOS } from "../../../../constants/url";
import { getElapsedHours } from "../utils/getElapsedHours";
import { getSemaforoColor } from "../utils/getSemaforoColor";
import { SEMAFORO_RULES, STATUS_MAP } from "../constants";

const obtenerOrdenDeColor = (colorClass) => {
  if (colorClass === "semaforo__rojo") return 1;
  if (colorClass === "semaforo__amarillo") return 2;
  if (colorClass === "semaforo__verde") return 3;
  if (colorClass === "semaforo__blanco") return 4;
  return 5;
};

const procesarServicio = (servicio, status) => {
  const fechaStatus = servicio[status.fechaCampo];

  if (!fechaStatus) {
    return null;
  }

  const horas = getElapsedHours(fechaStatus);
  const colorClass = getSemaforoColor(horas, SEMAFORO_RULES[status.estatusId]);

  return {
    ...servicio,
    horasTranscurridas: horas,
    colorClass,
    fechaStatus,
  };
};

const agruparPorEstatus = (servicios) => {
  const agrupados = [];
  const estatusExcluidos = [1, 7];

  for (let i = 0; i < STATUS_MAP.length; i++) {
    const status = STATUS_MAP[i];
    
    if (estatusExcluidos.includes(status.estatusId)) {
      continue;
    }

    const rule = SEMAFORO_RULES[status.estatusId];

    if (!rule) {
      console.warn(`No existe regla para estatus ${status.estatusId}`);
      continue;
    }

    const serviciosFiltrados = servicios.filter(
      (s) =>
        s.estatusId === status.estatusId &&
        s.anuladoGNP !== true &&
        s.liberadoProcesoAdministrativo !== true
    );

    if (serviciosFiltrados.length === 0) {
      agrupados.push({
        estatusId: status.estatusId,
        nombre: status.nombre,
        fechaCampo: status.fechaCampo,
        servicios: [],
        reglas: rule,
        total: 0,
      });
      continue;
    }

    const serviciosProcesados = [];
    for (let s = 0; s < serviciosFiltrados.length; s++) {
      const servicio = serviciosFiltrados[s];
      const servicioProcesado = procesarServicio(servicio, status);

      if (servicioProcesado) {
        serviciosProcesados.push(servicioProcesado);
      }
    }

    serviciosProcesados.sort((a, b) => {
      const ordenA = obtenerOrdenDeColor(a.colorClass);
      const ordenB = obtenerOrdenDeColor(b.colorClass);
      return ordenA - ordenB;
    });

    agrupados.push({
      estatusId: status.estatusId,
      nombre: status.nombre,
      fechaCampo: status.fechaCampo,
      servicios: serviciosProcesados,
      reglas: rule,
      total: serviciosProcesados.length,
    });
  }

  return agrupados;
};

const obtenerAsesoresUnicos = (servicios) => {
  const asesoresSet = new Set();

  for (let i = 0; i < servicios.length; i++) {
    const servicio = servicios[i];
    const asesor = servicio.asesorEscotel;

    if (!asesor || asesor.trim() === "") {
      asesoresSet.add("Sin asignación");
    } else {
      asesoresSet.add(asesor);
    }
  }

  const asesores = Array.from(asesoresSet).sort((a, b) => {
    if (a === "Sin asignación") return 1;
    if (b === "Sin asignación") return -1;
    return a.localeCompare(b);
  });
  return asesores;
};

const filtrarPorAsesor = (agrupados, asesorSeleccionado) => {
  if (asesorSeleccionado === "Todos") {
    return agrupados;
  }

  const agrupadosFiltrados = [];

  for (let i = 0; i < agrupados.length; i++) {
    const grupo = agrupados[i];
    const serviciosFiltrados = [];

    for (let s = 0; s < grupo.servicios.length; s++) {
      const servicio = grupo.servicios[s];
      const asesor = servicio.asesorEscotel;

      let asesorServicio = "";
      if (!asesor || asesor.trim() === "") {
        asesorServicio = "Sin asignación";
      } else {
        asesorServicio = asesor;
      }

      if (asesorServicio === asesorSeleccionado) {
        serviciosFiltrados.push(servicio);
      }
    }

    agrupadosFiltrados.push({
      ...grupo,
      servicios: serviciosFiltrados,
      total: serviciosFiltrados.length,
    });
  }

  return agrupadosFiltrados;
};

export const useSemaforoData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [agrupados, setAgrupados] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [asesorSeleccionado, setAsesorSeleccionado] = useState("Todos");

  const fetchServicios = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `${URL_CONCESIONARIOS}/GetServiciosByFiltro`;
      const body = { fechas: ["2019-01-01", "2029-01-01"] };
      const resultado = await post(url, body);

      if (resultado?.code !== 0 || !Array.isArray(resultado?.data)) {
        setError("Error al obtener servicios");
        setServicios([]);
        setAgrupados([]);
        setAsesores([]);
        setAsesorSeleccionado("Todos");
        return;
      }

      setServicios(resultado.data);
      
      const asesoresUnicos = obtenerAsesoresUnicos(resultado.data);
      setAsesores(asesoresUnicos);
      
      const gruposAgrupados = agruparPorEstatus(resultado.data);
      setAgrupados(gruposAgrupados);
      
      setAsesorSeleccionado("Todos");

    } catch (exception) {
      setError(exception.message || "Error al obtener servicios");
      setServicios([]);
      setAgrupados([]);
      setAsesores([]);
      setAsesorSeleccionado("Todos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServicios();
  }, [fetchServicios]);

  const agrupadosFiltrados = filtrarPorAsesor(agrupados, asesorSeleccionado);

  return {
    loading,
    error,
    fetchServicios,
    agrupados: agrupadosFiltrados,
    asesores,
    asesorSeleccionado,
    setAsesorSeleccionado,
  };
};
